import ApiService from "@/api";
import { ResponseModel } from "@/models";
import { EmployeeAccessModel, EmployeeModel, ExtensionModel, TemplateModel } from "../models";
import EnumUserStatus from "@/api/Main/dataSource/api/users/base/enums/EnumUserStatus";
import UpdateQueueTaskEmployeeRoleAccessListUserItem from "@/api/Main/dataSource/api/projects/performers/i-service/model/queue-task/request/UpdateQueueTaskEmployeeRoleAccessListUserItem";
import GetTemplateListRequest from "@/api/Main/dataSource/api/projects/groups/i-service/model/setting-tempalte/request/GetTemplateListRequest";
import RemoveTemplateRequest from "@/api/Main/dataSource/api/projects/groups/i-service/model/setting-tempalte/request/RemoveTemplateRequest";
import UpsertTemplateRequest from "@/api/Main/dataSource/api/projects/groups/i-service/model/setting-tempalte/request/UpsertTemplateRequest";
import { QueueSettingEnum } from "../enums";
import { TirPmNotificationEventBus, TirPmNotificationModel } from "tir-components";
import {
  mapGetEmployeesResponse,
  mapGetOwnedUsersResponse,
  mapGetUsersAccessResponse,
} from "./adapter-service.mappers";
import GetEmployeesListRequest from "@/api/Main/dataSource/api/projects/performers/i-service/model/request/GetEmployeesListRequest";
import PageRequest from "@/api/Main/dataSource/api/pagination-helper/lib/PageRequest";
import { CurrentUser, UserRoleEnum } from "tir-pm-user-core";
import GetQueueTaskEmployeeListDetailRequest from "@/api/Main/dataSource/api/projects/performers/i-service/model/queue-task/request/GetQueueTaskEmployeeListDetailRequest";
import UpdateQueueTaskEmployeeRoleAccessListRequest from "@/api/Main/dataSource/api/projects/performers/i-service/model/queue-task/request/UpdateQueueTaskEmployeeRoleAccessListRequest";
import GetListStatusRequest from "@/api/Main/dataSource/api/projects/task/i-service/models/status/GetListStatusRequest";
import GetPrioritiesListRequest from "@/api/Main/dataSource/api/projects/task/i-service/models/type-priority/GetPrioritiesListRequest";

/**Сервис для использования API редактирования очереди */
export class EditQueueAdapterService {
  /**API сервис */
  api = new ApiService();

  /**Изменить имя очереди */
  updateName(queueId: string, name: string) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectQueueTaskService.updateNameAsync({
        QueueTaskId: queueId,
        Name: name,
      })
        .then((response) => {
          result.Value = !!response;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          this.sendErrorNotification(result.ErrorMessage);
          console.error("ERROR updateName", e);
          return resolve(result);
        });
    });
  }
  /**Получение всех пользователей */
  getOwnedUsersAsync() {
    return new Promise<ResponseModel<EmployeeModel[]>>(async (resolve) => {
      const result = new ResponseModel<EmployeeModel[]>();
      await this.api.Main.PerformersListService.getOwnedUsersAsync({
        Search: "",
        StatusList: [EnumUserStatus.Active],
        Page: undefined,
      })
        .then((response) => {
          result.Value = mapGetOwnedUsersResponse(response);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          this.sendErrorNotification(result.ErrorMessage);
          console.error("ERROR getOwnedUsersAsync", e);
          return resolve(result);
        });
    });
  }

  /**Получение детальной информации о списке сотрудников рабочей области с доступами по очереди задач */
  async getUsersWorkSpaceAsync(
    req: GetQueueTaskEmployeeListDetailRequest,
  ): Promise<ResponseModel<EmployeeAccessModel[]>> {
    const result = new ResponseModel<EmployeeAccessModel[]>();

    try {
      const response =
        await this.api.Main.QueueTaskAdminRoleService.getQueueTaskEmployeeListDetailAsync(req);
      result.Value = mapGetUsersAccessResponse(response);
    } catch (e) {
      result.IsSuccess = false;
      result.ErrorCode = e?.ErrorCode;
      result.ErrorMessage = e?.ErrorMessage;
      this.sendErrorNotification(result.ErrorMessage);
      console.error("ERROR getOwnedUsersAsync", e);
    }

    return result;
  }

  /**обновление атрибутов доступов к рабочей области пользователей */
  async saveUsersAcessAsync(
    req: UpdateQueueTaskEmployeeRoleAccessListRequest,
  ): Promise<ResponseModel<Boolean>> {
    const result = new ResponseModel<Boolean>();

    try {
      const response =
        await this.api.Main.QueueTaskAdminRoleService.updateQueueTaskEmployeeRoleAccessListAsync(
          req,
        );
      result.Value = response;
    } catch (e) {
      result.IsSuccess = false;
      result.ErrorCode = e?.ErrorCode;
      result.ErrorMessage = e?.ErrorMessage;
      this.sendErrorNotification(result.ErrorMessage);
      console.error("ERROR getOwnedUsersAsync", e);
    }

    return result;
  }

  /**Получение удаленных из рабочей области пользователей */
  async getDeletedUsersWorkSpaceAsync(
    req: GetQueueTaskEmployeeListDetailRequest,
  ): Promise<ResponseModel<EmployeeModel[]>> {
    const result = new ResponseModel<EmployeeModel[]>();

    try {
      const response =
        await this.api.Main.QueueTaskAdminRoleService.getQueueTaskRemovedEmployeeListAsync(req);
      result.Value = mapGetOwnedUsersResponse(response);
    } catch (e) {
      result.IsSuccess = false;
      result.ErrorCode = e?.ErrorCode;
      result.ErrorMessage = e?.ErrorMessage;
      this.sendErrorNotification(result.ErrorMessage);
      console.error("ERROR getOwnedUsersAsync", e);
    }

    return result;
  }

  /**Получить список сотрудников текущего пользователя */
  private getEmployeesForManagerList() {
    return new Promise<ResponseModel<Array<EmployeeModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<EmployeeModel>>();

      await this.api.Main.EmployeeService.getEmployeesForManagerListAsync(
        new GetEmployeesListRequest({
          Page: new PageRequest({ Skip: 0, Take: 1000 }),
          Statuses: [EnumUserStatus.Active],
        }),
      )
        .then((resp) => {
          result.Value = mapGetEmployeesResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error("ERROR getEmployeesForManagerList", e);
          return resolve(result);
        });
    });
  }
  /**Получить всех пользователей */
  getUsers() {
    if (CurrentUser.Roles.includes(UserRoleEnum.Owner)) return this.getOwnedUsersAsync();
    return this.getEmployeesForManagerList();
  }
  /**Получение списка пользователей */
  getQueueTaskEmployeeList(queueId: string) {
    return new Promise<ResponseModel<EmployeeModel[]>>(async (resolve) => {
      const result = new ResponseModel<EmployeeModel[]>();
      await this.api.Main.QueueTaskAdminRoleService.getQueueTaskEmployeeListAsync({
        QueueTaskId: queueId,
        Page: undefined,
      })
        .then((response) => {
          result.Value = response.Items.map(
            (item) => new EmployeeModel({ ...(item as any), Id: item.UserId }),
          );
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          this.sendErrorNotification(result.ErrorMessage);
          console.error("ERROR getQueueTaskEmployeeList", e);
          return resolve(result);
        });
    });
  }
  /**Обновление всех сотрудников с доступом к очереди */
  updateQueueTaskEmployeeRoleList(queueId: string, usersIds: number[]) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.QueueTaskAdminRoleService.updateQueueTaskEmployeeRoleListAsync({
        QueueTaskId: queueId,
        UserIdList: usersIds,
      })
        .then((response) => {
          result.Value = !!response;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          this.sendErrorNotification(result.ErrorMessage);
          console.error("ERROR updateQueueTaskEmployeeRoleList", e);
          return resolve(result);
        });
    });
  }
  /**Тонкая настройка доступов пользователей к очереди */
  updateQueueTaskEmployeeRoleAccessList(queueId: string, users: EmployeeModel[]) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();
      await this.api.Main.QueueTaskAdminRoleService.updateQueueTaskEmployeeRoleAccessListAsync({
        QueueTaskId: queueId,
        Users: users.map(
          (u) =>
            new UpdateQueueTaskEmployeeRoleAccessListUserItem({
              UserId: u.Id,
              Accesses: u.Accesses as any,
            }),
        ),
      })
        .then((response) => {
          result.Value = !!response;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          this.sendErrorNotification(result.ErrorMessage);
          console.error("ERROR updateQueueTaskEmployeeRoleAccessList", e);
          return resolve(result);
        });
    });
  }
  /**Получить список шаблонов */
  getTemplateList(type: QueueSettingEnum) {
    return new Promise<ResponseModel<TemplateModel[]>>(async (resolve) => {
      const result = new ResponseModel<TemplateModel[]>();

      await this.api.Main.SettingQueueTaskTemplateService.getTemplateListAsync(
        new GetTemplateListRequest({ Type: type as any }),
      )
        .then((response) => {
          result.Value = response.Items.map((x) => new TemplateModel(x as any));
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          this.sendErrorNotification(result.ErrorMessage);
          console.error("ERROR updateQueueTaskEmployeeRoleList", e);
          return resolve(result);
        });
    });
  }
  /**Удалить шаблон */
  removeTemplate(templateId: string) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.SettingQueueTaskTemplateService.removeTemplateAsync(
        new RemoveTemplateRequest({
          TemplateId: templateId,
        }),
      )
        .then((response) => {
          result.Value = !!response;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          this.sendErrorNotification(result.ErrorMessage);
          console.error("ERROR updateQueueTaskEmployeeRoleList", e);
          return resolve(result);
        });
    });
  }
  /**Добавить/Обновить шаблон */
  upsertTemplate(template: TemplateModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.SettingQueueTaskTemplateService.upsertTemplateAsync(
        new UpsertTemplateRequest({
          TemplateId: template.Id,
          Name: template.Name,
          Data: template.Data,
          Type: template.Type as any,
        }),
      )
        .then((response) => {
          result.Value = !!response;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          this.sendErrorNotification(result.ErrorMessage);
          console.error("ERROR updateQueueTaskEmployeeRoleList", e);
          return resolve(result);
        });
    });
  }
  /**Получить список статусов */
  getStatuses(queueId: string) {
    return new Promise<ResponseModel<ExtensionModel[]>>(async (resolve) => {
      const result = new ResponseModel<ExtensionModel[]>();

      await this.api.Main.TaskStatusListService.getListAsync(new GetListStatusRequest({
        QueueTasksId: Array.isArray(queueId) ? queueId : [queueId],
        OrderByName: undefined,
        GlobalStatusIds: [],
        Search: "",
      }))
        .then((response) => {
          result.Value = response.Items.map(
            (x) =>
              new ExtensionModel({
                Id: x.Id,
                Color: x.Color,
                Name: x.Name,
                IsDefault: x.IsDefault,
                Description: x.Description,
              }),
          );
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          this.sendErrorNotification(result.ErrorMessage);
          console.error("ERROR updateQueueTaskEmployeeRoleList", e);
          return resolve(result);
        });
    });
  }
  /**Получить список приоритетов */
  getPriorities(queueId: string) {
    return new Promise<ResponseModel<ExtensionModel[]>>(async (resolve) => {
      const result = new ResponseModel<ExtensionModel[]>();

      await this.api.Main.TaskPriorityGetService.getPrioritiesListAsync(new GetPrioritiesListRequest({
        QueueTasksId: Array.isArray(queueId) ? queueId : [queueId],
        Search: "",
      }))
        .then((response) => {
          result.Value = response.map(
            (x) =>
              new ExtensionModel({
                Id: x.Id,
                Color: x.Color,
                Name: x.Name,
                IsDefault: x.IsDefault,
              }),
          );
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          this.sendErrorNotification(result.ErrorMessage);
          console.error("ERROR updateQueueTaskEmployeeRoleList", e);
          return resolve(result);
        });
    });
  }
  /**Отправка уведомления об ошибке */
  sendErrorNotification(message: string) {
    TirPmNotificationEventBus.emit(
      "add",
      new TirPmNotificationModel({
        title: message,
        type: "error",
        group: "general",
        life: 5000,
        closable: true,
        callbackButton: () => {},
      }),
    );
  }
}
